import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, type LicenceRow } from '../lib/api'
import { Empty, ErrorNote, Segmented } from '../components/Page'
import { Card, ConsoleHead, Field } from '../components/admin/Console'

type AccessModel = 'concurrent' | 'named_seat' | 'unlimited'

interface Draft {
  access_model: AccessModel
  seats: number
  idle_minutes: number
  hard_hours: number
  starts_on: string
  ends_on: string
  permits_download: boolean
  permits_export: boolean
  is_trial: boolean
}

function draftOf(licence: LicenceRow): Draft {
  return {
    access_model: (licence.access_model as AccessModel) ?? 'concurrent',
    seats:
      (licence.access_model === 'named_seat' ? licence.named_seat_limit : licence.concurrent_limit) ??
      1,
    idle_minutes: Math.round((licence.session_idle_timeout_s ?? 900) / 60),
    hard_hours: Math.round((licence.session_max_s ?? 14400) / 3600),
    starts_on: licence.starts_on ?? '',
    ends_on: licence.ends_on ?? '',
    permits_download: licence.permits_download,
    permits_export: licence.permits_export,
    is_trial: licence.is_trial,
  }
}

/**
 * Licence configuration.
 *
 * A licence is the whole access model in one record: who, over what dates,
 * how many at once, and with which rights. Everything on this form is enforced
 * server-side at the content-serving layer; nothing here is a client-side
 * gate, and a field the schema does not carry says so rather than pretending
 * to be editable.
 */
export function LicencesPage() {
  const queryClient = useQueryClient()
  const [selected, setSelected] = useState<string | null>(null)
  const [draft, setDraft] = useState<Draft | null>(null)

  const licences = useQuery({
    queryKey: ['admin', 'licences'],
    queryFn: () => api.get<LicenceRow[]>('/admin/licences'),
  })

  const licence = licences.data?.find((row) => row.id === selected) ?? licences.data?.[0]

  // The draft is seeded once per licence. A refetch must not overwrite what
  // the administrator has typed but not yet saved.
  const seeded = useRef<string | null>(null)
  useEffect(() => {
    if (licence && seeded.current !== licence.id) {
      seeded.current = licence.id
      setDraft(draftOf(licence))
    }
  }, [licence])

  const save = useMutation({
    mutationFn: (input: { id: string; body: Record<string, unknown> }) =>
      api.patch<LicenceRow>(`/admin/licences/${input.id}`, input.body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'licences'] }),
  })

  /**
   * Duplicating copies the terms, not the dates.
   *
   * A second institution buying the same thing is the ordinary case, and the
   * copy arrives suspended so nobody reads under a licence that was created by
   * a stray click.
   */
  const duplicate = useMutation({
    mutationFn: (source: LicenceRow) =>
      api.post<LicenceRow>('/admin/licences', {
        publication_id: source.publication_id,
        institution_id: source.institution_id,
        access_model: source.access_model,
        concurrent_limit: source.concurrent_limit,
        named_seat_limit: source.named_seat_limit,
        session_idle_timeout_s: source.session_idle_timeout_s,
        session_max_s: source.session_max_s,
        permits_download: source.permits_download,
        permits_export: source.permits_export,
        is_trial: source.is_trial,
        starts_on: source.starts_on,
        ends_on: source.ends_on,
        active: false,
      }),
    onSuccess: (created) => {
      setSelected(created.id)
      void queryClient.invalidateQueries({ queryKey: ['admin', 'licences'] })
    },
  })

  if (licences.isLoading) {
    return (
      <>
        <ConsoleHead title="Licence configuration" />
        <Card>
          <Empty>Reading the licences.</Empty>
        </Card>
      </>
    )
  }

  if (!licence || !draft) {
    return (
      <>
        <ConsoleHead title="Licence configuration" />
        <Card>
          <Empty>
            No licence exists yet. A publication becomes readable when an institution holds a
            licence over it, so the first step is to seed one: the bootstrap creates the development
            licence, and production licences are created against a signed agreement.
          </Empty>
        </Card>
      </>
    )
  }

  const unlimited = draft.access_model === 'unlimited'

  function submit() {
    if (!licence || !draft) return
    save.mutate({
      id: licence.id,
      body: {
        access_model: draft.access_model,
        concurrent_limit: draft.access_model === 'concurrent' ? draft.seats : null,
        named_seat_limit: draft.access_model === 'named_seat' ? draft.seats : null,
        session_idle_timeout_s: draft.idle_minutes * 60,
        session_max_s: draft.hard_hours * 3600,
        permits_download: draft.permits_download,
        permits_export: draft.permits_export,
        is_trial: draft.is_trial,
        starts_on: draft.starts_on || null,
        ends_on: draft.ends_on || null,
      },
    })
  }

  return (
    <>
      <ConsoleHead
        title="Licence configuration"
        note={`${licences.data?.length ?? 0} ${licences.data?.length === 1 ? 'licence' : 'licences'}`}
        actions={
          (licences.data?.length ?? 0) > 1 ? (
            <select
              className="input-dense w-auto"
              aria-label="Licence"
              value={licence.id}
              onChange={(event) => setSelected(event.target.value)}
            >
              {licences.data?.map((row) => (
                <option key={row.id} value={row.id}>
                  {row.institution_name} × {row.publication_name}
                </option>
              ))}
            </select>
          ) : undefined
        }
      />

      <ErrorNote error={save.error ?? duplicate.error} />

      <Card className="max-w-[560px] px-[19px] pb-[19px] pt-[17px]">
        <div className="mb-3.5 flex flex-wrap items-baseline gap-2.5">
          <h2 className="text-[20px] font-semibold leading-[1.15]">
            {licence.institution_name} × {licence.publication_name}
          </h2>
          <span
            className="rounded-[--radius-md] border px-1.5 py-[2px] font-mono text-[10px] uppercase"
            style={{
              background: licence.active ? 'var(--jl-accent-100)' : 'var(--color-accent-2-100)',
              borderColor: licence.active ? 'var(--jl-accent-200)' : 'var(--color-accent-2-700)',
              color: licence.active ? 'var(--jl-accent)' : 'var(--color-accent-2-700)',
            }}
          >
            {licence.active ? 'active' : 'suspended'}
          </span>
        </div>

        <div className="flex flex-col gap-[13px]">
          <Field label="Access model">
            {/* The board draws this edge to edge: three equal segments, one on. */}
            <div className="[&>[role=group]]:flex [&>[role=group]]:w-full [&_button]:flex-1">
              <Segmented<AccessModel>
                ariaLabel="Access model"
                value={draft.access_model}
                onChange={(value) => setDraft({ ...draft, access_model: value })}
                options={[
                  { value: 'concurrent', label: 'Concurrent' },
                  { value: 'named_seat', label: 'Named users' },
                  { value: 'unlimited', label: 'Unlimited' },
                ]}
              />
            </div>
          </Field>

          <Field
            label="User limit"
            hint={unlimited ? 'AN UNLIMITED LICENCE HAS NO USER COUNT TO ENFORCE.' : undefined}
          >
            <input
              className="input font-mono text-[12.5px]"
              type="number"
              min={1}
              disabled={unlimited}
              value={unlimited ? '' : draft.seats}
              onChange={(event) => setDraft({ ...draft, seats: Number(event.target.value) })}
              aria-label="User limit"
            />
          </Field>

          <Field label="Session timeout">
            <div className="flex items-center gap-2">
              <input
                className="input font-mono text-[12.5px]"
                type="number"
                min={1}
                value={draft.idle_minutes}
                onChange={(event) =>
                  setDraft({ ...draft, idle_minutes: Number(event.target.value) })
                }
                aria-label="Idle timeout in minutes"
              />
              <span className="flex-none font-mono text-[11.5px] text-ink2">min idle</span>
              <input
                className="input font-mono text-[12.5px]"
                type="number"
                min={1}
                value={draft.hard_hours}
                onChange={(event) => setDraft({ ...draft, hard_hours: Number(event.target.value) })}
                aria-label="Hard session limit in hours"
              />
              <span className="flex-none font-mono text-[11.5px] text-ink2">h hard</span>
            </div>
          </Field>

          <Field label="Date scope">
            <div className="flex items-center gap-2">
              <input
                className="input font-mono text-[12.5px]"
                type="date"
                value={draft.starts_on}
                onChange={(event) => setDraft({ ...draft, starts_on: event.target.value })}
                aria-label="Licence start date"
              />
              <span className="flex-none font-mono text-[11.5px] text-ink2">to</span>
              <input
                className="input font-mono text-[12.5px]"
                type="date"
                value={draft.ends_on}
                onChange={(event) => setDraft({ ...draft, ends_on: event.target.value })}
                aria-label="Licence end date"
              />
            </div>
          </Field>

          <Field
            label="Section scope"
            hint="THE SCHEMA CARRIES NO SECTION RESTRICTION YET, SO EVERY SECTION IS IN SCOPE."
          >
            <div className="input flex items-center font-mono text-[12.5px] text-ink2">
              All sections
            </div>
          </Field>

          <Field label="Rights" hint="TDM IS NOT A LICENCE FIELD YET; IT IS A ROLE PERMISSION.">
            <div className="input flex flex-wrap items-center gap-4 py-[7px]">
              {(
                [
                  ['permits_download', 'download'],
                  ['permits_export', 'export'],
                ] as const
              ).map(([key, label]) => (
                <label key={key} className="flex items-center gap-2 font-mono text-[12.5px]">
                  <input
                    type="checkbox"
                    checked={draft[key]}
                    style={{ accentColor: 'var(--jl-accent)' }}
                    onChange={(event) => setDraft({ ...draft, [key]: event.target.checked })}
                  />
                  {label}
                </label>
              ))}
            </div>
          </Field>

          <Field
            label="Trial"
            hint="A TRIAL IS A LICENCE WITH IS_TRIAL SET · SAME ENFORCEMENT, SAME REPORTING."
          >
            <div className="[&>[role=group]]:flex [&>[role=group]]:w-full [&_button]:flex-1">
              <Segmented<'no' | 'yes'>
                ariaLabel="Trial"
                value={draft.is_trial ? 'yes' : 'no'}
                onChange={(value) => setDraft({ ...draft, is_trial: value === 'yes' })}
                options={[
                  { value: 'no', label: 'A licence' },
                  { value: 'yes', label: 'A trial' },
                ]}
              />
            </div>
          </Field>
        </div>

        <div className="mt-4 flex flex-wrap gap-[7px] border-t border-n300 pt-3.5">
          <button type="button" className="btn-primary" disabled={save.isPending} onClick={submit}>
            {save.isPending ? 'Saving…' : 'Save licence'}
          </button>
          <button
            type="button"
            className="btn-secondary"
            disabled={duplicate.isPending}
            title="Copies these terms onto a new licence, suspended until you activate it."
            onClick={() => duplicate.mutate(licence)}
          >
            {duplicate.isPending ? 'Duplicating…' : 'Duplicate'}
          </button>
          <button
            type="button"
            className="btn-danger ml-auto"
            disabled={save.isPending}
            onClick={() =>
              save.mutate({ id: licence.id, body: { active: !licence.active } })
            }
          >
            {licence.active ? 'Suspend' : 'Reinstate'}
          </button>
        </div>
      </Card>
    </>
  )
}
